// thoughts.tsx

import React, { useState, useRef, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import "./answer/Answer.module.css";
import ChatCard from "./chat/ChatCard";
import { parseAnswerToHtml } from "./answer/AnswerParser";
import { TextAreaWithButton } from "./TextAreaWithButton";
import ShowCitationSource from "./chat/ShowCitationSource";
import ErrorBoundary from "../../utils/ErrorBoundry";
import { isValidEpochTime } from "../../utils/common";
import NoPage from "../NoPage";
import { RelatedCaseletProps } from "./chat/RenderFollowupCaselet";
import {
  checkFile,
  generateChat,
  getContextFromCacheMetaDataFile,
  getContextFromChatMetaDataFile,
  getFileChats,
  setContextInCacheMetaDataFile
} from "../../api/axiosInstanceApi";
import ContextDialog from "./ContextDialog";
import { messages } from "../../utils/constantMessages";

interface fetchGeneratedResponseUsingPromptProps {
  id: string | undefined;
  userInput: string;
}
interface ThoughtsProps {
  prompt: string;
  response: string | string[] | JSX.Element;
  citations?: string | string[] | JSX.Element[] | { text: string; link: string } | { text: string; link: string }[];
  followupQuestions?: string[];
  relatedCaselet?: RelatedCaseletProps[];
}

interface GeneratedChatListProps {
  generatedChat: JSX.Element[];
  globalIsLoading: boolean; // Add this prop
}

const GeneratedChatList = ({ generatedChat, globalIsLoading }: GeneratedChatListProps) => {
  return (
    <div className=" items-center justify-center mb-28 w-full  ">
      {generatedChat.map((chat, index) =>
        React.cloneElement(chat, {
          key: chat.key || index, // Ensure keys are stable
          disableFollowupQuestions: globalIsLoading // Pass the global loading state here
          // Any other props already passed to ChatCard should remain if they are static for that card
        })
      )}
    </div>
  );
};

const Thoughts = ({ prompt: initialPrompt }: ThoughtsProps) => {
  const [isError, setIsError] = useState(true);
  const [prompt, setPrompt] = useState(initialPrompt);
  const [generatedChat, setGeneratedChat] = useState<JSX.Element[]>([]);
  const location = useLocation();
  const lastCardRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false); // This is the key state
  const params = useParams();
  const [openRight, setOpenRight] = useState(false);
  const [isContextBoxVisible, setIsContextBoxVisible] = useState(false);
  const [isContextEditable, setIsContextEditable] = useState(true);
  const [contextValue, setContextValue] = useState<string>("");
  const [disableContextButton, setDisableContextButton] = useState(false);
  const [defaultRole, setDefaultRole] = useState(true);
  const [citation, setCitation] = useState();

  const indexRef = useRef(0);

  const openDrawerRight = () => {
    setOpenRight(true);
  };
  const closeDrawerRight = () => {
    setOpenRight(false);
    setShowCitationSource(false);
  };
  const [showCitationSource, setShowCitationSource] = useState(false);

  function onCitationClicked(citation: any) {
    setCitation(citation);
    setShowCitationSource(true);
    openDrawerRight();
  }

  async function onFollowupQuestionClicked(question: string) {
    if (isLoading) return;

    setIsLoading(true); // Start loading immediately

    await fetchGeneratedResponseUsingPrompt({
      id: params.thought_id,
      userInput: question
    });
  }

  const saveContextHandler = async (context: string) => {
    context === "" ? setDefaultRole(true) : setDefaultRole(false);
    await setContextInCacheMetaDataFile(context);
    setIsContextBoxVisible(false);
  };
  useEffect(() => {
    setShowCitationSource(false);
  }, [setShowCitationSource]);

  const getContextFromMetaDataFileHandler = async () => {
    const id = params.thought_id || "";
    let contextRes;
    if (id) contextRes = await getContextFromChatMetaDataFile(id);

    if (contextRes.data.status === "failed") {
      setIsContextEditable(true);
      const contextData = await getContextFromCacheMetaDataFile();
      if (contextData.data.context === "") {
        setContextValue(messages.DEFAULT_ROLE);
        setDefaultRole(true);
      } else {
        setContextValue(contextData.data.context);
        setDefaultRole(false);
      }
    } else {
      setIsContextEditable(false);
      if (contextRes.data.context == "") {
        setContextValue(messages.DEFAULT_ROLE);
        setDefaultRole(true);
      } else {
        setContextValue(contextRes.data.context);
        setDefaultRole(false);
      }
    }
  };
  useEffect(() => {
    if (isContextBoxVisible) {
      getContextFromMetaDataFileHandler();
    }
  }, [isContextBoxVisible]);

  async function fetchAndSetThoughts(id: string | undefined) {
    try {
      setIsLoading(true); // Indicate loading while fetching history

      let checkFileStatus = await checkFile(id);
      const fileExist = checkFileStatus.data;
      if (!fileExist.status) {
        const cacheData = await getContextFromCacheMetaDataFile();
        if (cacheData.status === 201) {
          setIsContextBoxVisible(true);
          setContextValue(messages.DEFAULT_ROLE);
          setDefaultRole(true);
        } else if (cacheData.status === 200) {
          setIsContextBoxVisible(false);
          if (cacheData.data.context == "") {
            setContextValue(messages.DEFAULT_ROLE);
            setDefaultRole(true);
          } else {
            setContextValue(cacheData.data.context);
            setDefaultRole(false);
          }
        }
        throw new Error("File not exists.");
      } else {
        setIsContextBoxVisible(false);
        setIsContextEditable(false);
      }

      const historyResponse = await getFileChats(id);

      if (historyResponse == null) {
        throw new Error();
      }
      const data = historyResponse.data.conversation;
      const context = historyResponse.data.context;

      if (context === undefined) {
        setDisableContextButton(true);
        setContextValue(messages.DEFAULT_ROLE);
        setDefaultRole(true);
      } else {
        setDisableContextButton(false);
        setDefaultRole(false);
        getContextFromMetaDataFileHandler();
      }

      const newChats: JSX.Element[] = [];

      for (const item of data) {
        const citationWithLink = item.citations;
        const { answerHtml, followupQuestions, relatedCaselets } = parseAnswerToHtml(item.response, item.followup_questions, item.caselets);

        let feed_back_value = "not_available";
        if (item.feed_back === undefined || item.feed_back === "") {
          feed_back_value = "not_available";
        } else {
          feed_back_value = item.feed_back;
        }

        const chatCardElement = (
          <ChatCard
            key={indexRef.current}
            index={indexRef.current}
            prompt={item.query}
            feedBack={feed_back_value}
            response={answerHtml}
            citations={citationWithLink}
            isLoading={false} // Historical chats are not individually loading
            followupQuestions={citationWithLink.length !== 0 ? followupQuestions : []}
            relatedCaselet={relatedCaselets}
            conversation_id={id}
            onCitationClick={onCitationClicked}
            onFollowupQuestionClicked={onFollowupQuestionClicked}
            isOldInteractions={false}
            // REMOVE disableFollowupQuestions={true} or {isLoading} from here
            // It will be added dynamically by GeneratedChatList
          />
        );
        newChats.push(chatCardElement);
        indexRef.current++;
      }
      setGeneratedChat(prevChat => [...prevChat, ...newChats]);
    } catch (err) {
      console.error("Error fetching and setting thoughts:", err);
    } finally {
      setIsLoading(false); // End loading after history is fetched
    }
  }

  const fetchDataAndGenerateCard = async (id: string | undefined) => {
    await fetchGeneratedResponseUsingPrompt({ id: id, userInput: prompt.trim() });
  };

  async function fetchGeneratedResponseUsingPrompt({ id, userInput }: fetchGeneratedResponseUsingPromptProps) {
    try {
      setIsLoading(true); // Start loading for new response

      const initialEmptyCard = (
        <ChatCard
          key={generatedChat.length}
          prompt={userInput}
          response=""
          citations={[]}
          isLoading={true} // This specific card is loading
          isOldInteractions={true}
          // REMOVE disableFollowupQuestions from here
          // It will be added dynamically by GeneratedChatList
        />
      );

      setGeneratedChat(previousChatCard => [...previousChatCard, initialEmptyCard]);

      const queryResponse = await generateChat(id, userInput);
      const result = queryResponse.data;

      const citationWithLink = result.citations;
      let updatedCard: JSX.Element;
      if (result.response && result.response.trim().length > 0) {
        const { answerHtml, followupQuestions, relatedCaselets } = parseAnswerToHtml(
          result.response,
          result.followup_questions,
          result.caselets
        );
        updatedCard = (
          <ChatCard
            key={generatedChat.length}
            prompt={userInput}
            index={indexRef.current}
            response={answerHtml}
            feedBack={""}
            citations={citationWithLink}
            conversation_id={id}
            isLoading={false} // Response received, this specific card is no longer loading
            followupQuestions={citationWithLink.length !== 0 ? followupQuestions : []}
            relatedCaselet={relatedCaselets}
            onCitationClick={onCitationClicked}
            onFollowupQuestionClicked={onFollowupQuestionClicked}
            isOldInteractions={true}
            // REMOVE disableFollowupQuestions from here
            // It will be added dynamically by GeneratedChatList
          />
        );
      } else {
        updatedCard = (
          <ChatCard
            key={generatedChat.length}
            prompt={userInput}
            response=""
            conversation_id={id}
            isLoading={false} // Error, this specific card is no longer loading
            isResponseEmptyFlag={true}
            // REMOVE disableFollowupQuestions from here
            // It will be added dynamically by GeneratedChatList
          />
        );
      }
      setGeneratedChat(prevChat => {
        const updatedChat = [...prevChat];
        updatedChat[updatedChat.length - 1] = updatedCard;
        return updatedChat;
      });

      setIsContextEditable(false);
    } catch (err) {
      console.log("error in fetch and generate card", err);
      const errorCard = (
        <ChatCard
          key={generatedChat.length}
          prompt={userInput}
          response={"<div className='flex  justify-center h-44 w-full font-bold p-6'>Oops, something went wrong  </div>"}
          citations={[]}
          followupQuestions={[]}
          isLoading={false}
          relatedCaselet={[]}
          isResponseEmptyFlag={true}
          // REMOVE disableFollowupQuestions from here
          // It will be added dynamically by GeneratedChatList
        />
      );
      setGeneratedChat(prevChat => {
        const updatedChat = [...prevChat];
        updatedChat[updatedChat.length - 1] = errorCard;
        return updatedChat;
      });
    } finally {
      indexRef.current++;
      setIsLoading(false); // End loading for new response
    }
  }

  useEffect(() => {
    if (isValidEpochTime(params.thought_id)) {
      fetchAndSetThoughts(params.thought_id);
    } else {
      setIsError(false);
      console.log("iserror: ", isError);
    }
  }, [params.thought_id, location.state]);

  useEffect(() => {
    if (lastCardRef.current) {
      lastCardRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [generatedChat]);

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };

  const generateResponse = async () => {
    if (isLoading) return;

    setPrompt("");
    setIsLoading(true); // Start loading for manual prompt generation

    const from = location.state?.from ?? "individual";
    if (from === "new") {
      try {
        const checkFileStatus = await checkFile(params.thought_id);
        const fileExist = checkFileStatus.data;

        if (!fileExist.status) {
          indexRef.current = 0;
        }
        await getFileChats(params.thought_id);

        await fetchDataAndGenerateCard(params.thought_id);
      } catch (error) {
        console.error("Error in generateResponse (new):", error);
        setIsLoading(false); // Ensure loading is off if initial checks fail
        try {
          await fetchAndSetThoughts(params.thought_id);
          await fetchDataAndGenerateCard(params.thought_id);
        } catch (innerError) {
          console.error("Something went wrong with fallback:", innerError);
          setIsLoading(false);
        }
      }
    } else {
      if (isValidEpochTime(params.thought_id)) {
        await fetchDataAndGenerateCard(params.thought_id);
      } else {
        setIsError(false);
        setIsLoading(false);
      }
    }
  };
  return (
    <div className="flex  h-full">
      {isContextBoxVisible && (
        <ContextDialog
          isContextBoxVisible={isContextBoxVisible}
          setIsContextBoxVisible={setIsContextBoxVisible}
          isContextEditable={isContextEditable}
          saveContextHandler={saveContextHandler}
          contextValue={contextValue}
          setContextValue={setContextValue}
          defaultRole={defaultRole}
        ></ContextDialog>
      )}

      {isError ? (
        <ErrorBoundary fallback="Data not found">
          <div className="shrink-0 pb-14 w-full ">
            <GeneratedChatList generatedChat={generatedChat} globalIsLoading={isLoading} />

            <TextAreaWithButton
              prompt={prompt}
              onChange={handlePromptChange}
              onGenerateResponse={generateResponse}
              contextValue={contextValue}
              isContextEditable={isContextEditable}
              setIsContextBoxVisible={setIsContextBoxVisible}
              disableContextButton={disableContextButton}
              isLoading={isLoading}
            />
          </div>
          {showCitationSource && (
            <div className="citation-source flex-shrink-0">
              <ShowCitationSource style={{ flexGrow: 1 }} open={openRight} onClose={closeDrawerRight} citation={citation} />
            </div>
          )}
        </ErrorBoundary>
      ) : (
        <div className="flex items-center justify-center w-full ">
          <NoPage />
        </div>
      )}
    </div>
  );
};

export default Thoughts;
